import * as Sentry from '@sentry/react'

const FORMATTER_CACHE = new Map<string, Intl.DateTimeFormat>()

const cacheKey = (options: Intl.DateTimeFormatOptions, timeZone?: string): string => {
  const config = Object.entries(options)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([option, value]) => `${option}:${value}`)
    .join(',')
  return `${config}|${timeZone ?? ''}`
}

const createFormatter = (options: Intl.DateTimeFormatOptions, timeZone?: string): Intl.DateTimeFormat => {
  if (!timeZone) {
    return new Intl.DateTimeFormat('en-US', options)
  }
  try {
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone })
  } catch (error) {
    Sentry.captureException(error, { extra: { timeZone } })
    return new Intl.DateTimeFormat('en-US', options)
  }
}

export const getDateTimeFormatter = (options: Intl.DateTimeFormatOptions, timeZone?: string): Intl.DateTimeFormat => {
  const key = cacheKey(options, timeZone)
  const cached = FORMATTER_CACHE.get(key)
  if (cached) {
    return cached
  }
  const formatter = createFormatter(options, timeZone)
  FORMATTER_CACHE.set(key, formatter)
  return formatter
}

const CANONICAL_TIMEZONE_CACHE = new Map<string, string | null>()

export const canonicalTimezone = (timeZone: string): string | null => {
  const cached = CANONICAL_TIMEZONE_CACHE.get(timeZone)
  if (cached !== undefined) {
    return cached
  }
  let canonical: string | null
  try {
    canonical = new Intl.DateTimeFormat('en-US', { timeZone }).resolvedOptions().timeZone
  } catch {
    canonical = null
  }
  CANONICAL_TIMEZONE_CACHE.set(timeZone, canonical)
  return canonical
}

export const isSameTimezone = (a: string, b: string): boolean => {
  if (a === b) {
    return true
  }
  const canonicalA = canonicalTimezone(a)
  const canonicalB = canonicalTimezone(b)
  return canonicalA !== null && canonicalB !== null && canonicalA === canonicalB
}
